/*
 * Fix Alignment — DeckorateAPI client (AIVA-5381, against the AIVA-5458 API).
 *
 * Talks to POST /v1/deckorate/alignment/fix on the Deckorate content backend. The endpoint
 * takes the slide's geometry and returns a list of corrections to apply back
 * onto the shapes:
 *   - mode "fast" — the instruction matched a deterministic command
 *     ("align left", "distribute vertically", …), computed server-side.
 *   - mode "ai"   — anything else (including our default instruction) goes
 *     through the LLM layout pass.
 *
 * Field naming mirrors the FastAPI models exactly: the request/response
 * envelope is snake_case (`request_id`), while the nested slide/shape/
 * correction objects use camelCase aliases (`widthPts`, `hasText`, `fontSize`).
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { logError, scrubMessage } from "../../../../../telemetry";
import type { AlignmentScope, SlideSnapshot } from "./slideGeometry";

/** connectorSystemName tag for all Fix Alignment telemetry. */
const FIX_ALIGNMENT_CONNECTOR = "deckorate";

/** The DeckorateAPI route this feature drives. */
const ALIGNMENT_FIX_PATH = "/v1/deckorate/alignment/fix";

/** How long to wait for the layout pass before giving up on it. */
export const ALIGNMENT_REQUEST_TIMEOUT_MS = 90_000;

export const NO_SHAPES_TO_ALIGN_MESSAGE = "There are no shapes on this slide to align.";

/**
 * The default instruction. Deliberately NOT one of the backend's fast-command
 * phrases — this card is the mockup's one-shot "Fix alignment" action, so it
 * must take the AI layout pass that repairs rows, columns, spacing, overlaps
 * and overflow rather than a single deterministic align.
 */
export const FIX_ALIGNMENT_INSTRUCTION = "fix alignment and spacing";

/** Matches AlignmentRequest.shapes' max_length — the request is 422'd above it. */
export const MAX_SHAPES_PER_REQUEST = 200;

/** Matches ShapeGeometry.text's max_length. */
export const MAX_SHAPE_TEXT_CHARS = 2000;

/** Centralized so every catch site in this feature extracts messages the same way. */
export function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** Reports a Fix Alignment failure through the shared telemetry pipeline — never `console.*`. */
export function logFixAlignmentError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "FixAlignmentError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: FIX_ALIGNMENT_CONNECTOR, reason },
  );
}

/**
 * Builds a `.catch` handler that reports an otherwise-unhandled rejection.
 * The panel's click handlers already surface their own errors via UI state, so
 * this only fires if a future change makes one throw before its try/catch runs
 * — it exists so that case is still reported rather than silently swallowed.
 */
export function reportFixAlignmentFailure(reason: string): (error: unknown) => void {
  return (error: unknown) => logFixAlignmentError(reason, getErrorMessage(error, "Unexpected error"));
}

/* ── Error copy ──────────────────────────────────────────────────────────────
 * createHttpClient throws `Deckorate POST /alignment/fix: <status> <rawBody>`,
 * so a failure would otherwise reach the panel as a status code stapled to a
 * JSON envelope. describeAlignmentFailure() turns that into a sentence: the
 * API's own `message` when it explains something the user can act on, and
 * plain-English copy for the classes where it doesn't.
 */

/**
 * createHttpClient's message prefix and the status that follows the first
 * ": " after it. Parsed with indexOf + an anchored digit match rather than one
 * regex over the whole string: the body is arbitrary JSON, and a pattern
 * combining `\s*` with a trailing `[\s\S]*$` backtracks super-linearly on it.
 */
const HTTP_ERROR_PREFIX = "Deckorate ";
const HTTP_ERROR_SEPARATOR = ": ";
const LEADING_STATUS_PATTERN = /^\d{3}/;

interface ParsedHttpError {
  readonly status: number;
  readonly body: string;
}

function parseHttpError(raw: string): ParsedHttpError | null {
  if (!raw.startsWith(HTTP_ERROR_PREFIX)) return null;
  // The first ": " separates "<METHOD> <path>" from "<status> <body>"; later
  // ones belong to the JSON body and must not be matched.
  const separator = raw.indexOf(HTTP_ERROR_SEPARATOR, HTTP_ERROR_PREFIX.length);
  if (separator === -1) return null;

  const remainder = raw.slice(separator + HTTP_ERROR_SEPARATOR.length);
  const status = LEADING_STATUS_PATTERN.exec(remainder);
  if (!status) return null;

  return { status: Number(status[0]), body: remainder.slice(status[0].length).trim() };
}

/** Status codes this feature reacts to, named so the branches aren't magic numbers. */
const HTTP_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  serverError: 500,
  badGateway: 502,
} as const;

const UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
const UPSTREAM_MESSAGE =
  "The layout service couldn't produce a usable result for this slide. Nothing was changed — try again.";
const SERVER_MESSAGE = "The alignment service is unavailable right now. Nothing was changed.";
export const TIMEOUT_MESSAGE =
  "The alignment service took too long to respond. Nothing was changed — try again.";
export const CANCELLED_MESSAGE = "Fix alignment was cancelled. Nothing was changed.";

/** Pull `message` out of either error envelope DeckorateAPI can return. */
function apiErrorMessage(rawBody: string): string | null {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    if (typeof parsed !== "object" || parsed === null) return null;
    const body = parsed as { message?: unknown; error?: { message?: unknown } };
    // Route-level failures: { request_id, code, error, message }
    if (typeof body.message === "string" && body.message) return body.message;
    // Handler-level failures: { error: { code, message, details, request_id } }
    const nested = body.error?.message;
    return typeof nested === "string" && nested ? nested : null;
  } catch {
    return null;
  }
}

/** True when a rejection came from an AbortController rather than the server. */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/**
 * Turn a thrown request failure into copy worth showing in the panel.
 *
 * `timedOut` distinguishes the two reasons a request can abort — our own
 * deadline elapsed, or the user navigated away — since they warrant different
 * copy but produce the same AbortError.
 */
export function describeAlignmentFailure(
  error: unknown,
  fallback: string,
  options: { timedOut?: boolean } = {},
): string {
  if (isAbortError(error)) return options.timedOut ? TIMEOUT_MESSAGE : CANCELLED_MESSAGE;

  const raw = getErrorMessage(error, fallback);
  const parsed = parseHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiMessage = apiErrorMessage(parsed.body);

  if (status === HTTP_STATUS.unauthorized || status === HTTP_STATUS.forbidden) {
    return UNAUTHORIZED_MESSAGE;
  }
  // 502/504 mean the model returned something unusable, or the API rejected it
  // for moving a shape off-slide or distorting a locked picture. The API's
  // message names which, so it's worth showing.
  if (status >= HTTP_STATUS.badGateway) {
    return apiMessage ? `${UPSTREAM_MESSAGE} (${apiMessage})` : UPSTREAM_MESSAGE;
  }
  if (status >= HTTP_STATUS.serverError) return SERVER_MESSAGE;
  // 4xx is a payload problem — the API's message says exactly what.
  return apiMessage ?? raw;
}

interface AlignmentApiShape {
  id: string;
  name: string;
  type: string;
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
  hasText: boolean;
  fontSize: number | null;
  isImage: boolean;
  aspectLocked: boolean;
  aspectRatio: number | null;
}

interface AlignmentApiRequest {
  request_id?: string;
  instruction: string;
  scope: AlignmentScope;
  slide: { widthPts: number; heightPts: number };
  shapes: AlignmentApiShape[];
}

/** One shape's new geometry. Omitted properties mean "leave that one alone". */
export interface AlignmentCorrection {
  readonly id: string;
  readonly left?: number;
  readonly top?: number;
  readonly width?: number;
  readonly height?: number;
  readonly fontSize?: number;
}

export interface AlignmentResult {
  readonly requestId: string;
  readonly mode: AlignmentMode;
  readonly message: string;
  readonly corrections: readonly AlignmentCorrection[];
}

/** Deliberately `unknown`-typed: this is wire data, validated before use. */
interface RawAlignmentResponse {
  request_id?: unknown;
  success?: unknown;
  mode?: unknown;
  message?: unknown;
  corrections?: unknown;
}

export type AlignmentMode = "fast" | "ai";

/** A Set (not an array) since this is only ever an existence check. */
const ALIGNMENT_MODES = new Set<string>(["fast", "ai"]);

/** The mode reported when the API sends one outside the known set. */
const DEFAULT_MODE: AlignmentMode = "ai";

/**
 * Geometry keys a correction may carry. `fontSize` is bounded by the API's own
 * schema (8–72); the rest are free floats validated only for finiteness here,
 * since the API has already checked them against slide bounds.
 */
const GEOMETRY_KEYS = ["left", "top", "width", "height"] as const;
const MIN_CORRECTION_FONT_SIZE = 8;
const MAX_CORRECTION_FONT_SIZE = 72;

/**
 * Render an unexpected value for a log line. Objects go through JSON.stringify
 * so they read as their contents rather than "[object Object]", which would
 * make the telemetry event useless for diagnosing the drift. Also used for
 * Office.js throwables, which are not `Error` instances.
 */
export function formatUnknown(value: unknown): string {
  if (typeof value === "string") return value;
  if (value === null || typeof value !== "object") return String(value);
  try {
    return JSON.stringify(value) ?? "unserialisable";
  } catch {
    return "unserialisable";
  }
}

function isPositionValue(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** width/height must stay positive — a zero or negative size would corrupt the shape. */
function isSizeValue(value: unknown): value is number {
  return isPositionValue(value) && value > 0;
}

function isFontSizeValue(value: unknown): value is number {
  return (
    isPositionValue(value) &&
    value >= MIN_CORRECTION_FONT_SIZE &&
    value <= MAX_CORRECTION_FONT_SIZE
  );
}

/**
 * Validate one wire correction, keeping only the properties that are safe to
 * write to a shape. Returns null when the entry carries no usable id, or nothing
 * actionable at all — applying a correction with no geometry would be a no-op
 * that still counted as "adjusted".
 */
function toValidCorrection(raw: unknown): AlignmentCorrection | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  const id = typeof entry.id === "string" ? entry.id.trim() : "";
  if (!id) return null;

  const correction: { -readonly [K in keyof AlignmentCorrection]?: AlignmentCorrection[K] } = { id };
  for (const key of GEOMETRY_KEYS) {
    const value = entry[key];
    if (value === undefined || value === null) continue;
    const valid = key === "width" || key === "height" ? isSizeValue(value) : isPositionValue(value);
    if (valid) correction[key] = value as number;
  }
  if (entry.fontSize !== undefined && entry.fontSize !== null && isFontSizeValue(entry.fontSize)) {
    correction.fontSize = entry.fontSize;
  }

  const hasGeometry = GEOMETRY_KEYS.some((key) => correction[key] !== undefined);
  if (!hasGeometry && correction.fontSize === undefined) return null;
  return correction as AlignmentCorrection;
}

/**
 * Normalise a response into an AlignmentResult, dropping anything unusable.
 * The API validates its own output, so a rejected entry here means a malformed
 * or intercepted response (a proxy error page, a truncated body) — surviving
 * that with the good entries beats failing the whole action, but it is reported
 * so the drift is visible instead of silent.
 */
export function normaliseAlignmentResult(raw: RawAlignmentResponse | null | undefined): AlignmentResult {
  const body = raw ?? {};
  const isKnownMode = typeof body.mode === "string" && ALIGNMENT_MODES.has(body.mode);
  const mode = isKnownMode ? (body.mode as AlignmentMode) : DEFAULT_MODE;
  if (body.mode !== undefined && !isKnownMode) {
    logFixAlignmentError(
      "fix-alignment-unknown-mode",
      `Unknown alignment mode: ${formatUnknown(body.mode)}`,
    );
  }

  const rawCorrections = Array.isArray(body.corrections) ? body.corrections : [];
  if (body.corrections !== undefined && !Array.isArray(body.corrections)) {
    logFixAlignmentError("fix-alignment-bad-corrections", "corrections was not an array");
  }

  const corrections = rawCorrections
    .map(toValidCorrection)
    .filter((correction): correction is AlignmentCorrection => correction !== null);
  const dropped = rawCorrections.length - corrections.length;
  if (dropped > 0) {
    logFixAlignmentError(
      "fix-alignment-invalid-corrections",
      `Discarded ${dropped} of ${rawCorrections.length} alignment correction(s) as unusable`,
    );
  }

  return {
    requestId: typeof body.request_id === "string" ? body.request_id : "",
    mode,
    message: typeof body.message === "string" ? body.message : "",
    corrections,
  };
}

/**
 * `request_id` is a non-secret correlation id the backend echoes back for
 * request/response tracing. The API defaults it to a server-side uuid4 when
 * absent, so on a host without `crypto.randomUUID` we simply omit it rather
 * than inventing a weaker id.
 */
function newRequestId(): string | undefined {
  return globalThis.crypto?.randomUUID?.();
}

function toApiShapes(snapshot: SlideSnapshot): AlignmentApiShape[] {
  return snapshot.shapes.slice(0, MAX_SHAPES_PER_REQUEST).map((shape) => ({
    id: shape.id,
    name: shape.name,
    type: shape.type,
    text: shape.text.slice(0, MAX_SHAPE_TEXT_CHARS),
    left: shape.left,
    top: shape.top,
    width: shape.width,
    height: shape.height,
    hasText: shape.hasText,
    fontSize: shape.fontSize,
    isImage: shape.isImage,
    aspectLocked: shape.aspectLocked,
    aspectRatio: shape.aspectRatio,
  }));
}

/**
 * POST the slide geometry and return the validated corrections.
 *
 * Rejects with an empty-shape guard rather than letting the API 422 on it:
 * AlignmentRequest.shapes has min_length 1, and a caller with nothing to send
 * has nothing to fix either.
 */
export async function postFixAlignment(
  http: HttpClient,
  snapshot: SlideSnapshot,
  scope: AlignmentScope,
  options: HttpRequestOptions = {},
): Promise<AlignmentResult> {
  const shapes = toApiShapes(snapshot);
  if (shapes.length === 0) throw new Error(NO_SHAPES_TO_ALIGN_MESSAGE);

  const requestId = newRequestId();
  const request: AlignmentApiRequest = {
    ...(requestId ? { request_id: requestId } : {}),
    instruction: FIX_ALIGNMENT_INSTRUCTION,
    scope,
    slide: { widthPts: snapshot.slide.widthPts, heightPts: snapshot.slide.heightPts },
    shapes,
  };

  const response = await http.post<RawAlignmentResponse | null>(
    ALIGNMENT_FIX_PATH,
    request,
    options,
  );
  return normaliseAlignmentResult(response);
}
